const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN = 11;

function cleanAscii(value) {
  return String(value ?? '')
    .replace(/[\r\n]+/g, ' ')
    .replace(/[–—]/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/•/g, '-')
    .replace(/[^\x20-\x7E]/g, '?');
}

function pdfEscape(value) {
  return cleanAscii(value)
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
}

function fmt(number) {
  return Number(Number(number).toFixed(2)).toString();
}

class PageCanvas {
  constructor() { this.ops = []; }
  line(x1, y1, x2, y2, width = 0.8) {
    this.ops.push(`${fmt(width)} w ${fmt(x1)} ${fmt(y1)} m ${fmt(x2)} ${fmt(y2)} l S`);
  }
  rect(x, y, width, height, lineWidth = 0.8) {
    this.ops.push(`${fmt(lineWidth)} w ${fmt(x)} ${fmt(y)} ${fmt(width)} ${fmt(height)} re S`);
  }
  text(value, x, y, size = 9, bold = false) {
    const text = cleanAscii(value);
    if (!text) return;
    this.ops.push(`BT /${bold ? 'F2' : 'F1'} ${fmt(size)} Tf 1 0 0 1 ${fmt(x)} ${fmt(y)} Tm (${pdfEscape(text)}) Tj ET`);
  }
  signature(strokes, x, y, width, height) {
    (strokes || []).forEach(stroke => {
      if (!Array.isArray(stroke) || stroke.length < 2) return;
      const points = stroke.map(point => ({
        x: x + (Math.max(0, Math.min(1, Number(point?.x) || 0)) * width),
        y: y + ((1 - Math.max(0, Math.min(1, Number(point?.y) || 0))) * height),
      }));
      const [first, ...rest] = points;
      this.ops.push(`1.25 w 1 J 1 j ${fmt(first.x)} ${fmt(first.y)} m ${rest.map(point => `${fmt(point.x)} ${fmt(point.y)} l`).join(' ')} S`);
    });
  }
  center(value, y, size = 14, bold = true) {
    const text = cleanAscii(value);
    const estimated = text.length * size * 0.51;
    this.text(text, (PAGE_W - estimated) / 2, y, size, bold);
  }
  stream() { return this.ops.join('\n'); }
}

function textWidthEstimate(text, fontSize) {
  return cleanAscii(text).length * fontSize * 0.48;
}

function wrapText(text, maxWidth, fontSize = 7.4) {
  const words = cleanAscii(text).trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [''];
  const lines = [];
  let line = '';
  words.forEach(word => {
    const next = line ? `${line} ${word}` : word;
    if (line && textWidthEstimate(next, fontSize) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  return lines;
}

function localDateTime(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw) ? `${raw}:00` : raw;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function activityTimeLabel(value) {
  const date = localDateTime(value);
  if (!date) return cleanAscii(value);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}${mi}`;
}

function preparedLabel(date = new Date()) {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yyyy} ${hh}${mi}`;
}

function displayDate(value) {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : raw;
}

function displayTime(value) {
  return String(value || '').replace(':', '').trim();
}

function commonTop(page, daily, incident) {
  page.center('ACTIVITY LOG (ICS 214)', 768, 15, true);
  const top = 749;
  const bottom = 711;
  page.rect(MARGIN, bottom, PAGE_W - (2 * MARGIN), top - bottom, 1.1);
  page.line(238, bottom, 238, top, 1.1);

  page.text('1. Incident Name:', MARGIN + 6, top - 13, 8.8, true);
  page.text(incident?.name || '', MARGIN + 8, bottom + 7, 9.2);

  page.text('2. Operational Period:', 244, top - 13, 8.8, true);
  page.text('Date From:', 352, top - 13, 8.2);
  page.text(displayDate(daily?.fromDate), 407, top - 13, 8.5);
  page.text('Date To:', 497, top - 13, 8.2);
  page.text(displayDate(daily?.toDate), 540, top - 13, 8.5);
  page.text('Time From:', 370, top - 28, 8.2);
  page.text(displayTime(daily?.fromTime), 426, top - 28, 8.5);
  page.text('Time To:', 512, top - 28, 8.2);
  page.text(displayTime(daily?.toTime), 554, top - 28, 8.5);

  return bottom;
}

function preparedBlock(page, daily, bottom = 14) {
  const top = bottom + 34;
  page.rect(MARGIN, bottom, PAGE_W - (2 * MARGIN), 34, 1.0);
  page.line(248, bottom, 248, top, 1.0);
  page.line(420, bottom, 420, top, 1.0);
  page.text('8. Prepared by:', MARGIN + 6, top - 12, 8.4, true);
  page.text('Name:', 92, top - 12, 8.3);
  page.text(daily?.preparedBy || daily?.name || '', 128, top - 12, 8.5);
  page.text('Position/Title:', 254, top - 12, 8.3);
  page.text(daily?.preparedPosition || daily?.icsPosition || '', 328, top - 12, 8.5);
  page.text('Date/Time:', 254, bottom + 7, 8.3);
  page.text(daily?.preparedAtLabel || preparedLabel(), 310, bottom + 7, 8.1);
  page.text('Signature:', 426, top - 10, 7.8, true);
  page.signature(daily?.signatureStrokes || [], 472, bottom + 4, 120, 24);
}

function activitiesWithUnits(activities) {
  return [...(activities || [])]
    .sort((left, right) => String(left?.at || '').localeCompare(String(right?.at || '')))
    .map(activity => {
    const lines = wrapText(activity?.description || '', 446, 7.4);
    return { ...activity, lines, units: Math.max(1, lines.length) };
  });
}

function takeByUnits(items, maxUnits) {
  const pages = [];
  let current = [];
  let used = 0;

  items.forEach(item => {
    let lineOffset = 0;
    while (lineOffset < item.lines.length) {
      const room = maxUnits - used;
      if (room <= 0) {
        pages.push(current);
        current = [];
        used = 0;
        continue;
      }

      const take = Math.min(room, item.lines.length - lineOffset);
      const slice = {
        ...item,
        lines: item.lines.slice(lineOffset, lineOffset + take),
        units: take,
        continued: Boolean(item.continued || lineOffset > 0),
      };
      current.push(slice);
      used += take;
      lineOffset += take;

      if (used >= maxUnits) {
        pages.push(current);
        current = [];
        used = 0;
      }
    }
  });

  if (current.length || !pages.length) pages.push(current);
  return pages;
}

function drawActivityRows(page, items, topY, bottomY, unitCount) {
  const rowHeight = (topY - bottomY) / unitCount;
  const dateColumn = MARGIN + 103;
  page.line(dateColumn, bottomY, dateColumn, topY, 0.7);

  let y = topY;
  let unitsDrawn = 0;

  items.forEach(item => {
    for (let index = 0; index < item.units; index += 1) {
      y -= rowHeight;
      page.line(MARGIN, y, PAGE_W - MARGIN, y, 0.45);
      const baseline = y + rowHeight - 9.3;
      if (index === 0) {
        page.text(item.continued ? '(cont.)' : activityTimeLabel(item.at), MARGIN + 5, baseline, 7.3);
      }
      page.text(item.lines[index] || '', dateColumn + 6, baseline, 7.4);
      unitsDrawn += 1;
    }
  });

  while (unitsDrawn < unitCount) {
    y -= rowHeight;
    page.line(MARGIN, y, PAGE_W - MARGIN, y, 0.45);
    unitsDrawn += 1;
  }
}

function pageOne(daily, incident, items) {
  const page = new PageCanvas();
  const topBottom = commonTop(page, daily, incident);

  const infoBottom = 672;
  page.rect(MARGIN, infoBottom, PAGE_W - (2 * MARGIN), topBottom - infoBottom, 1.0);
  page.line(208, infoBottom, 208, topBottom, 0.9);
  page.line(408, infoBottom, 408, topBottom, 0.9);
  page.text('3. Name:', MARGIN + 6, topBottom - 13, 8.5, true);
  page.text(daily?.name || '', MARGIN + 8, infoBottom + 6, 9);
  page.text('4. ICS Position:', 214, topBottom - 13, 8.5, true);
  page.text(daily?.icsPosition || '', 216, infoBottom + 6, 9);
  page.text('5. Home Agency (and Unit):', 414, topBottom - 13, 8.5, true);
  page.text(daily?.homeAgency || '', 416, infoBottom + 6, 8.1);

  const resourcesTop = 672;
  const titleBottom = 654;
  const headerBottom = 636;
  const resourcesBottom = 522;
  page.rect(MARGIN, resourcesBottom, PAGE_W - (2 * MARGIN), resourcesTop - resourcesBottom, 1.0);
  page.line(MARGIN, titleBottom, PAGE_W - MARGIN, titleBottom, 0.75);
  page.line(MARGIN, headerBottom, PAGE_W - MARGIN, headerBottom, 0.75);
  page.text('6. Resources Assigned:', MARGIN + 6, resourcesTop - 13, 8.5, true);

  const x1 = 208;
  const x2 = 408;
  page.line(x1, resourcesBottom, x1, titleBottom, 0.7);
  page.line(x2, resourcesBottom, x2, titleBottom, 0.7);
  page.text('Name', 101, titleBottom - 12, 8.2);
  page.text('ICS Position', 277, titleBottom - 12, 8.2);
  page.text('Home Agency (and Unit)', 451, titleBottom - 12, 8.2);

  const resourceRows = 8;
  const resourceRowHeight = (headerBottom - resourcesBottom) / resourceRows;
  for (let index = 1; index <= resourceRows; index += 1) {
    page.line(MARGIN, headerBottom - (index * resourceRowHeight), PAGE_W - MARGIN, headerBottom - (index * resourceRowHeight), 0.45);
  }
  (daily?.resources || []).slice(0, 8).forEach((resource, index) => {
    const y = headerBottom - ((index + 1) * resourceRowHeight) + 4.4;
    page.text(resource?.name || '', MARGIN + 5, y, 7.4);
    page.text(resource?.icsPosition || '', x1 + 5, y, 7.4);
    page.text(resource?.homeAgency || '', x2 + 5, y, 7.0);
  });

  const activityTop = resourcesBottom;
  const columnTop = 504;
  const rowsTop = 487;
  const rowsBottom = 64;
  page.rect(MARGIN, rowsBottom, PAGE_W - (2 * MARGIN), activityTop - rowsBottom, 1.0);
  page.line(MARGIN, columnTop, PAGE_W - MARGIN, columnTop, 0.7);
  page.line(MARGIN, rowsTop, PAGE_W - MARGIN, rowsTop, 0.7);
  page.text('7. Activity Log:', MARGIN + 6, activityTop - 13, 8.5, true);
  page.text('Date/Time', 50, columnTop - 12, 8.1);
  page.text('Notable Activities', 121, columnTop - 12, 8.1);
  drawActivityRows(page, items, rowsTop, rowsBottom, 24);

  preparedBlock(page, daily, 14);
  page.text('ICS 214, Page 1', MARGIN + 6, 17, 8.1, true);
  return page.stream();
}

function continuationPage(daily, incident, items, pageNumber) {
  const page = new PageCanvas();
  const topBottom = commonTop(page, daily, incident);
  const headingBottom = 692;
  const columnBottom = 675;
  const rowsBottom = 64;

  page.rect(MARGIN, rowsBottom, PAGE_W - (2 * MARGIN), topBottom - rowsBottom, 1.0);
  page.line(MARGIN, headingBottom, PAGE_W - MARGIN, headingBottom, 0.7);
  page.line(MARGIN, columnBottom, PAGE_W - MARGIN, columnBottom, 0.7);
  page.text('7. Activity Log (continuation):', MARGIN + 6, topBottom - 13, 8.5, true);
  page.text('Date/Time', 49, headingBottom - 12, 8.1);
  page.text('Notable Activities', 121, headingBottom - 12, 8.1);
  drawActivityRows(page, items, columnBottom, rowsBottom, 36);

  preparedBlock(page, daily, 14);
  page.text(`ICS 214, Page ${pageNumber}`, MARGIN + 6, 17, 8.1, true);
  return page.stream();
}

function buildPdf(contentStreams) {
  const objects = [];
  const add = value => {
    objects.push(value);
    return objects.length;
  };

  const fontRegular = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  const fontBold = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>');
  const pageObjectIds = [];
  const streamIds = [];

  contentStreams.forEach(stream => {
    const byteLength = new TextEncoder().encode(stream).length;
    streamIds.push(add(`<< /Length ${byteLength} >>\nstream\n${stream}\nendstream`));
    pageObjectIds.push(add('PENDING'));
  });

  const pagesId = objects.length + 1;
  add(`<< /Type /Pages /Kids [${pageObjectIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pageObjectIds.length} >>`);

  pageObjectIds.forEach((objectId, index) => {
    objects[objectId - 1] = `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Resources << /Font << /F1 ${fontRegular} 0 R /F2 ${fontBold} 0 R >> >> /Contents ${streamIds[index]} 0 R >>`;
  });

  const catalogId = add(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);
  let pdf = '%PDF-1.4\n%LineMedic-ICS214\n';
  const offsets = [0];

  objects.forEach((object, index) => {
    offsets.push(new TextEncoder().encode(pdf).length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefOffset = new TextEncoder().encode(pdf).length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach(offset => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;

  return new TextEncoder().encode(pdf);
}

export function generateIcs214Pdf({ incident, daily }) {
  const items = activitiesWithUnits(daily?.activities || []);
  const firstPage = takeByUnits(items, 24);
  const firstItems = firstPage.shift() || [];
  const remaining = firstPage.flatMap(group => group);
  const continuationGroups = takeByUnits(remaining, 36);

  const preparedDaily = {
    ...daily,
    preparedBy: daily?.preparedBy || daily?.name || '',
    preparedPosition: daily?.preparedPosition || daily?.icsPosition || '',
    preparedAtLabel: daily?.signedAt
      ? preparedLabel(new Date(daily.signedAt))
      : preparedLabel(),
    signatureStrokes: Array.isArray(daily?.signatureStrokes) ? daily.signatureStrokes : [],
  };

  const streams = [pageOne(preparedDaily, incident || {}, firstItems)];
  if (remaining.length) {
    continuationGroups.forEach((group, index) => {
      streams.push(continuationPage(preparedDaily, incident || {}, group, index + 2));
    });
  }
  return buildPdf(streams);
}

export function ics214FileName(incident, daily) {
  const safe = value => cleanAscii(value)
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 70);
  const incidentPart = safe(incident?.name || 'Incident') || 'Incident';
  const opPart = safe(daily?.opLabel || daily?.fromDate || 'Daily') || 'Daily';
  return `ICS-214_${incidentPart}_${opPart}.pdf`;
}

export function pdfBytesToBase64(bytes) {
  let binary = '';
  const chunk = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunk));
  }
  return btoa(binary);
}

export async function shareOrOpenIcs214(bytes, filename) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const file = typeof File !== 'undefined' ? new File([blob], filename, { type: 'application/pdf' }) : null;

  if (file && navigator?.share && navigator?.canShare?.({ files: [file] })) {
    await navigator.share({ files: [file], title: filename });
    return;
  }

  const url = URL.createObjectURL(blob);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) window.location.assign(url);
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}
